(function () {
  'use strict';

  const API_URL = '/api/2026-10/graphql.json';
  const DEFAULT_TIMEOUT = 5000;
  const catalogs = new Map();
  const preloads = new WeakMap();
  const query = `query BedPriceCatalog($handle: String!, $after: String, $country: CountryCode!)
    @inContext(country: $country) {
      product(handle: $handle) {
        id
        variants(first: 250, after: $after) {
          nodes { id availableForSale price { amount currencyCode } }
          pageInfo { hasNextPage endCursor }
        }
      }
    }`;

  function pricingError(message) {
    const error = new Error(message);
    error.code = 'pricing';
    return error;
  }

  function abortReason(signal) {
    if (signal.reason !== undefined) return signal.reason;
    const error = new Error('Bed price preparation was cancelled.');
    error.name = 'AbortError';
    return error;
  }

  function checkActive(signal) {
    if (signal?.aborted) throw abortReason(signal);
  }

  function copyMap(map) {
    return Object.freeze(Object.fromEntries(Object.entries(map || {})));
  }

  function positiveId(value) {
    if (typeof value === 'string' && /^\d+$/.test(value)) value = Number(value);
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }

  function moneyCents(amount) {
    // MoneyV2 amounts are decimal strings. Never round a fractional cent or
    // allow floating-point multiplication to change an exact catalog price.
    const match = typeof amount === 'string' && /^(\d+)(?:\.(\d+))?$/.exec(amount);
    if (!match || /[1-9]/.test((match[2] || '').slice(2))) {
      throw pricingError('A bed catalog price is invalid.');
    }
    const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0').slice(0, 2));
    if (!Number.isSafeInteger(cents)) throw pricingError('A bed catalog price is invalid.');
    return cents;
  }

  function contextFor(configurator) {
    const pricing = configurator?.pricing;
    // Other currencies deliberately retain the existing Liquid catalog.
    if (pricing?.enabled !== true || pricing.currency !== 'EUR') return null;
    if (!/^[A-Z]{2}$/.test(pricing.country || '') ||
        !Array.isArray(pricing.sourceProducts) || !pricing.sourceProducts.length) {
      throw pricingError('The bed price catalog is unavailable.');
    }
    const sources = pricing.sourceProducts.map(source => {
      if (typeof source?.handle !== 'string' || !source.handle.trim()) {
        throw pricingError('A bed price catalog product is missing.');
      }
      return { id: String(source.id || ''), handle: source.handle };
    });
    const context = { country: pricing.country, currency: pricing.currency, sources };
    context.key = JSON.stringify(context);
    return context;
  }

  async function loadProduct(source, context, signal) {
    const variants = [];
    const cursors = new Set();
    let after = null;
    do {
      checkActive(signal);
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { handle: source.handle, after, country: context.country } }),
        signal,
        cache: 'no-store',
      });
      checkActive(signal);
      if (!response.ok) throw pricingError('The bed price catalog could not be loaded.');
      const result = await response.json();
      checkActive(signal);
      const connection = result?.data?.product?.variants;
      if (result?.errors?.length || !Array.isArray(connection?.nodes) ||
          typeof connection?.pageInfo?.hasNextPage !== 'boolean' ||
          (source.id && result.data.product.id !== source.id)) {
        throw pricingError('The bed price catalog is incomplete.');
      }
      for (const variant of connection.nodes) {
        const idMatch = typeof variant?.id === 'string' && /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(variant.id);
        const id = idMatch && positiveId(idMatch[1]);
        if (!id || typeof variant.availableForSale !== 'boolean' || variant.price?.currencyCode !== context.currency) {
          throw pricingError('A bed catalog variant is invalid.');
        }
        const price = moneyCents(variant.price.amount);
        if (variant.availableForSale) variants.push({ id, price });
      }
      if (!connection.pageInfo.hasNextPage) break;
      after = connection.pageInfo.endCursor;
      if (typeof after !== 'string' || !after || cursors.has(after)) {
        throw pricingError('The bed price catalog pagination is invalid.');
      }
      cursors.add(after);
    } while (true);
    return variants;
  }

  async function loadCatalog(context, signal) {
    // Fetch independent products together, but merge in declared source order,
    // preserving the existing deterministic last-product-wins price mapping.
    const products = await Promise.all(context.sources.map(source => loadProduct(source, context, signal)));
    checkActive(signal);
    const map = {};
    for (const variants of products) {
      for (const variant of variants) map[variant.price] = variant.id;
    }
    return copyMap(map);
  }

  async function prepare(configurator, { signal, timeoutMs = DEFAULT_TIMEOUT } = {}) {
    checkActive(signal);
    const legacy = copyMap(configurator?.variants);
    let context;
    try { context = contextFor(configurator); } catch (error) { return legacy; }
    if (!context) return legacy;

    let entry = catalogs.get(context.key);
    if (!entry) {
      entry = { generation: 0, map: null };
      catalogs.set(context.key, entry);
    }
    const generation = ++entry.generation;
    const controller = new AbortController();
    const onParentAbort = () => controller.abort(abortReason(signal));
    signal?.addEventListener('abort', onParentAbort, { once: true });
    const duration = Number.isFinite(timeoutMs) && timeoutMs >= 0 ? timeoutMs : DEFAULT_TIMEOUT;
    const timer = setTimeout(() => controller.abort(pricingError('The bed price catalog timed out.')), duration);
    let onAbort;
    const aborted = new Promise((resolve, reject) => {
      onAbort = () => reject(abortReason(controller.signal));
      controller.signal.addEventListener('abort', onAbort, { once: true });
    });

    try {
      checkActive(signal);
      const map = await Promise.race([loadCatalog(context, controller.signal), aborted]);
      checkActive(signal);
      checkActive(controller.signal);
      if (generation === entry.generation) entry.map = map;
      return copyMap(map);
    } catch (error) {
      // A cart cancellation must never turn into a successful fallback submit.
      checkActive(signal);
      return copyMap(entry.map || legacy);
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onParentAbort);
      controller.signal.removeEventListener('abort', onAbort);
      // A failing parallel product must not leave other pages downloading after
      // this attempt has already returned its fallback.
      if (!controller.signal.aborted) controller.abort();
    }
  }

  function preload(configurator) {
    // Background initialization is opportunistic and cannot leak a rejection.
    if (!configurator || typeof configurator !== 'object') return Promise.resolve();
    if (!preloads.has(configurator)) {
      preloads.set(configurator, prepare(configurator).then(() => undefined, () => undefined));
    }
    return preloads.get(configurator);
  }

  function resolve(totalPrice, variants) {
    const calculatedPrice = typeof totalPrice === 'string' && /^\d+$/.test(totalPrice)
      ? Number(totalPrice) : totalPrice;
    if (!Number.isSafeInteger(calculatedPrice) || calculatedPrice < 0) {
      throw pricingError('The bed price is invalid.');
    }
    // Cached and Liquid fallback catalogs obey the same rule: the purchased
    // variant must cost exactly what the shopper sees. Never round down.
    const variantId = Object.hasOwn(variants || {}, calculatedPrice)
      ? positiveId(variants[calculatedPrice]) : null;
    if (!variantId) throw pricingError('No sellable variant exactly matches this bed price.');
    return { variantId, variantPrice: calculatedPrice, calculatedPrice, matchType: 'exact' };
  }

  window.MaisonBedPrices = Object.freeze({ preload, prepare, resolve });
  // Alpine can initialize before this deferred body asset. The root also calls
  // preload for the opposite script order; the WeakMap makes both paths safe.
  if (window.maisonBedConfigurator) preload(window.maisonBedConfigurator);
})();
